/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import type { TypeActe } from '@/types';

/**
 * Valeurs du dropdown de civilité de la lib (`OPTIONS_SEXE`, adossé à son enum
 * `Sexe`). Écrites en littéral car cet enum n'est **pas** réexporté par
 * `@mairistem/administres-utils` ; `utils/payloads/formatters.formatSexe`
 * compare déjà à `'homme'` de la même manière.
 */
type ValeurSexe = 'homme' | 'femme';

export interface RoleParent {
  /** Rôle dans les titres du `ParentForm` de la lib. */
  libelle: string;
  titreSection: string;
  /**
   * Rôle précédé de son article, pour les phrases (« Modifier le père »).
   * Stocké plutôt que dérivé : le genre ne se déduit pas du libellé.
   */
  libelleAvecArticle: string;
  /** Civilité imposée par le rôle, convertie en code INSEE par `formatSexe`. */
  sexe: ValeurSexe;
}

/**
 * Rôles « père / mère » : les colonnes d'acte de la reconnaissance portent le
 * rôle (`ar_per_*` / `ar_mer_*`) et non un rang, la civilité en découle donc.
 */
const ROLES_PERE_MERE: Record<1 | 2, RoleParent> = {
  1: {
    libelle: 'Père',
    titreSection: 'INFORMATIONS SUR LE PÈRE',
    libelleAvecArticle: 'le père',
    sexe: 'homme',
  },
  2: {
    libelle: 'Mère',
    titreSection: 'INFORMATIONS SUR LA MÈRE',
    libelleAvecArticle: 'la mère',
    sexe: 'femme',
  },
};

/**
 * Rôle de chaque parent selon le type d'acte. Un type absent garde le
 * comportement historique : « PARENT 1 / PARENT 2 » et civilité libre.
 *
 * ⚠️ Conséquence assumée là où des rôles sont imposés : la civilité étant
 * verrouillée, `*_sexe_parents` vaut toujours `MXT` — deux parents de même sexe
 * n'y sont plus saisissables. Décision produit ; la naissance garde le choix.
 */
const ROLES_PARENTS_PAR_TYPE_ACTE: Partial<
  Record<TypeActe, Record<1 | 2, RoleParent>>
> = {
  reconnaissance: ROLES_PERE_MERE,
  /*
   * ⚠️ Le DÉCÈS n'est délibérément PAS ici, bien que `ec_actd` ait aussi des
   * colonnes `ad_per_*` / `ad_mer_*` : décision produit, il garde
   * « PARENT 1 / PARENT 2 » et une civilité libre, comme la naissance. Ajouter
   * une entrée verrouillerait la civilité — ce qui n'est pas voulu ici.
   */
};

/**
 * Libellés « le père » / « la mère » d'un type d'acte, ou `undefined` s'il n'a
 * pas de rôles imposés. Évite aux appelants une assertion non nulle sur le
 * registre, que biome interdit.
 */
export const libellesParentsAvecArticle = (
  typeActe: TypeActe,
): Record<1 | 2, string> | undefined => {
  const roles = ROLES_PARENTS_PAR_TYPE_ACTE[typeActe];

  return roles
    ? { 1: roles[1].libelleAvecArticle, 2: roles[2].libelleAvecArticle }
    : undefined;
};

/** Libellés d'onglet (majuscules) d'un type d'acte à rôles imposés. */
export const libellesParentsOnglet = (
  typeActe: TypeActe,
): Record<1 | 2, string> | undefined => {
  const roles = ROLES_PARENTS_PAR_TYPE_ACTE[typeActe];

  return roles
    ? {
        1: roles[1].libelle.toUpperCase(),
        2: roles[2].libelle.toUpperCase(),
      }
    : undefined;
};

export default ROLES_PARENTS_PAR_TYPE_ACTE;
